import { z } from 'zod';
import { CareDocumentDataSchema } from '../CareDocumentDataSchema';
import { IObject } from '../../../IObject';
import {
  dateValidation,
  fullNameValidation,
  requiredString,
  coerceNumber,
} from '../../../../validation';

// The weekdays of one phased-return week, in template order (the document
// pre-prints Monday–Friday in every week row).
export const returnToWorkPlanWeekDays = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
] as const;

export type ReturnToWorkPlanWeekDay = (typeof returnToWorkPlanWeekDays)[number];

export const displayReturnToWorkPlanWeekDay: Record<
  ReturnToWorkPlanWeekDay,
  string
> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
};

// One row of the "3. Key Medical Considerations" table.
export const ReturnToWorkPlanMedicalConsiderationSchema = z.object({
  consideration: requiredString('Consideration is required'),
  description: requiredString('Consideration description is required'),
});

// An empty form input carries '' — that day is simply not worked, so it is
// stored as null rather than read as a number.
const blankToNull = (value: unknown) =>
  value === '' || value === null ? null : value;

// Working hours of one weekday; null = not working that day.
const ScheduleDayHoursSchema = z
  .preprocess(
    blankToNull,
    coerceNumber(
      z
        .number('Working hours must be a number')
        .min(0, 'Working hours must not be negative')
        .max(24, 'Working hours must not exceed 24'),
    ).nullable(),
  )
  .default(null);

// Working hours per weekday of one phased-return week.
export const ReturnToWorkPlanScheduleDaysSchema = z.object({
  monday: ScheduleDayHoursSchema,
  tuesday: ScheduleDayHoursSchema,
  wednesday: ScheduleDayHoursSchema,
  thursday: ScheduleDayHoursSchema,
  friday: ScheduleDayHoursSchema,
});

export type ReturnToWorkPlanScheduleDays = z.infer<
  typeof ReturnToWorkPlanScheduleDaysSchema
>;

// The week's total hours are DERIVED from the per-day hours (never stored);
// tolerant of in-flight form values, which are strings while editing.
export function returnToWorkPlanWeekTotalHours(
  days:
    | Partial<Record<ReturnToWorkPlanWeekDay, number | string | null>>
    | null
    | undefined,
): number {
  return returnToWorkPlanWeekDays.reduce((sum, day) => {
    const value = days?.[day];
    if (value === null || value === undefined || value === '') {
      return sum;
    }
    const hours = Number(value);
    return Number.isFinite(hours) ? sum + hours : sum;
  }, 0);
}

// One week of the "5. Phased Return Schedule" table; the week number is the
// 1-based position in the schedule array.
export const ReturnToWorkPlanScheduleWeekSchema = z.object({
  weekCommencing: dateValidation('Week commencing date is required'),
  days: ReturnToWorkPlanScheduleDaysSchema,
  comments: z.string().default(''),
});

// The Aviva-only "Suggested Phased Return to Work Plan" (PPL-3293). The
// "Review Date" cell of the Employee Details table is deliberately NOT here —
// the generated document leaves it blank for the line manager to complete.
export const ReturnToWorkPlanDataSchema = CareDocumentDataSchema.extend({
  patient: z.object({
    fullName: fullNameValidation('Employee name is required'),
    dob: dateValidation('Employee date of birth is required'),
    work: z.object({
      jobTitle: requiredString('Job title is required'),
      normalWorkingPattern: requiredString(
        'Normal working pattern is required',
      ),
      lineManager: z.string().default(''),
      hrContact: z.string().default(''),
      // "If company has OH"
      occupationalHealthAdvisor: z.string().default(''),
    }),
  }),
  diagnosis: requiredString('Diagnosis is required'),
  treatmentReceived: requiredString('Treatment received is required'),
  dateOfPlan: dateValidation('Date of plan is required'),
  plannedStartDate: dateValidation('Planned start date is required'),
  // "2. Purpose of This Plan" placeholder inputs — the surrounding copy is a
  // fixed part of the template.
  purpose: z.object({
    // "...return to work following treatment for X"
    treatmentFor: requiredString('Treatment description is required'),
    // "...rebuild capacity and confidence over a N week period"
    periodWeeks: coerceNumber(
      z
        .number('Number of weeks is required')
        .int('Number of weeks must be a whole number')
        .min(1, 'Number of weeks must be at least 1'),
    ),
    // "...effects of cancer treatment, including X"
    treatmentEffects: requiredString('Treatment effects are required'),
  }),
  medicalConsiderations: z
    .array(ReturnToWorkPlanMedicalConsiderationSchema)
    .min(1, 'At least one medical consideration is required'),
  recommendedAdjustments: z
    .array(z.string().min(1, 'Adjustment must not be empty'))
    .min(1, 'At least one recommended adjustment is required'),
  schedule: z
    .array(ReturnToWorkPlanScheduleWeekSchema)
    .min(1, 'At least one schedule week is required'),
});

export type ReturnToWorkPlanMedicalConsideration = z.infer<
  typeof ReturnToWorkPlanMedicalConsiderationSchema
>;
export type ReturnToWorkPlanScheduleWeek = z.infer<
  typeof ReturnToWorkPlanScheduleWeekSchema
>;
export type ReturnToWorkPlanData = z.infer<typeof ReturnToWorkPlanDataSchema>;
export type ReturnToWorkPlan = IObject.HasIdTimestamped<ReturnToWorkPlanData>;
